// Custom drawer component for free gifts that persists in DOM
// This prevents state loss when drawer opens/closes

import { animate as motionAnimate, timeline as motionTimeline } from 'vendor';
import { FocusTrap, Delegate } from 'vendor';

var reduceDrawerAnimation =
  window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
  window.themeVariables?.settings?.reduceDrawerAnimation;

class FreeGiftsDrawer extends HTMLElement {
  static get observedAttributes() {
    return ['id', 'open'];
  }
  
  static #lockLayerCount = 0;
  
  #isLocked = false;
  
  constructor() {
    super();
    this.addEventListener('dialog:force-close', (event) => {
      this.hide();
      event.stopPropagation();
    });
    
    // Create shadow DOM with drawer template
    const template = document.getElementById('drawer-default-template')?.content.cloneNode(true);
    if (template) {
      this.attachShadow({ mode: 'open' }).appendChild(template);
      this.shadowRoot.addEventListener('slotchange', (event) =>
        this._updateSlotVisibility(event.target)
      );
    }
    
    // Update viewport height
    document.documentElement.style.setProperty(
      '--viewport-height',
      `${window.innerHeight}px`
    );
    this.updateViewportHeight();
    window.addEventListener('resize', () => this.updateViewportHeight());
  }
  
  updateViewportHeight() {
    document.documentElement.style.setProperty(
      '--viewport-height',
      `${window.innerHeight}px`
    );
  }
  
  connectedCallback() {
    this.reduceAnimation = this.hasAttribute('drawer-reduce-animation') ? true : reduceDrawerAnimation;
    this.setAttribute('aria-modal', 'true');
    this.setAttribute('role', 'dialog');
    
    if (this.shadowRoot) {
      this.shadowRoot
        .querySelector('[part="overlay"]')
        ?.addEventListener('click', this.hide.bind(this));
      
      Array.from(this.shadowRoot.querySelectorAll('slot')).forEach((slot) =>
        this._updateSlotVisibility(slot)
      );
    }
    
    // Setup event listeners
    this._abortController = new AbortController();
    this._setupEventListeners();
    
    // Listen for cart changes to reset selections after add-to-cart
    this._boundHandleCartChange = this._handleCartChange.bind(this);
    document.addEventListener('cart:change', this._boundHandleCartChange);
  }
  
  disconnectedCallback() {
    if (this._abortController) {
      this._abortController.abort();
    }
    this._cleanupEventListeners();
    this.focusTrap?.deactivate({ onDeactivate: () => {} });
    
    // Remove cart change listener
    if (this._boundHandleCartChange) {
      document.removeEventListener('cart:change', this._boundHandleCartChange);
      this._boundHandleCartChange = null;
    }
    
    if (this.#isLocked) {
      this.#isLocked = false;
      FreeGiftsDrawer.#lockLayerCount = Math.max(0, FreeGiftsDrawer.#lockLayerCount - 1);
      document.documentElement.classList.toggle('lock', FreeGiftsDrawer.#lockLayerCount > 0);
    }
  }
  
  get template() {
    return this.getAttribute('template') || 'drawer-default-template';
  }
  
  get shouldLock() {
    return true;
  }
  
  // CRITICAL: Override to prevent moving to body - keeps drawer in DOM
  get shouldAppendToBody() {
    return false; // Never move to body, stay in original position
  }
  
  get shouldTrapFocus() {
    return true;
  }
  
  get openFrom() {
    return window.matchMedia(`${window.themeVariables?.breakpoints?.['sm-max'] || '(max-width: 699px)'}`)
      .matches
      ? 'bottom'
      : this.getAttribute('open-from') || 'right';
  }
  
  get open() {
    return this.hasAttribute('open');
  }
  
  get controls() {
    return Array.from(this.getRootNode().querySelectorAll(`[aria-controls="${this.id}"]`));
  }
  
  get delegate() {
    return (this._delegate = this._delegate || new Delegate(document.body));
  }
  
  get initialFocus() {
    return this.hasAttribute('initial-focus')
      ? this.getAttribute('initial-focus')
      : this.hasAttribute('tabindex')
      ? this
      : this.querySelector('input:not([type="hidden"])') || false;
  }
  
  get preventScrollWhenTrapped() {
    return true;
  }
  
  get focusTrap() {
    return (this._focusTrap =
      this._focusTrap ||
      new FocusTrap.createFocusTrap(this, {
        onDeactivate: this.hide.bind(this),
        allowOutsideClick: this._allowOutsideClick.bind(this),
        initialFocus: window.matchMedia('screen and (pointer: fine)').matches
          ? this.initialFocus
          : false,
        fallbackFocus: this,
        tabbableOptions: {
          getShadowRoot: true,
        },
        preventScroll: this.preventScrollWhenTrapped,
      }));
  }
  
  _setupEventListeners() {
    if (!this.id) return;
    this._cleanupEventListeners();
    this._delegate = new Delegate(document.body);
    this._delegate.on('click', `[aria-controls="${this.id}"]`, this._onToggleClicked.bind(this));
  }
  
  _cleanupEventListeners() {
    if (this._delegate) {
      this._delegate.off();
      this._delegate = null;
    }
  }
  
  /**
   * Handle cart:change events — reset all gift selections after add-to-cart.
   * Uses double requestAnimationFrame to run after free-gift-item's toggleInputState
   * (which also uses requestAnimationFrame), ensuring our reset is the final state.
   */
  _handleCartChange(event) {
    if (event?.detail?.baseEvent !== 'variant:add') return;
    
    // Double RAF: free-gift-item uses requestAnimationFrame in handleCartChange
    // to call toggleInputState (which re-checks items in cart). We run after that
    // so our unchecked state wins.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.resetSelections();
      });
    });
  }
  
  /**
   * Reset all gift checkbox selections within this drawer.
   * Unchecks every checkbox, clears pending-change flags, removes disabled styling,
   * and notifies child components (free-gifts-value, free-gifts-button) to update.
   */
  resetSelections() {
    const giftItems = this.querySelectorAll('free-gift-item');
    giftItems.forEach((item) => {
      const input = item.querySelector('input[type="checkbox"]');
      if (input) {
        input.checked = false;
        input.disabled = false;
      }
      // Clear pending changes so future toggleInputState calls
      // sync normally with cart state
      item._hasPendingChanges = false;
      item.classList.remove('is-disabled');
    });
    
    // Update free-gifts-value components so the displayed total resets
    const valueElements = this.querySelectorAll('free-gifts-value');
    valueElements.forEach((el) => {
      if (typeof el.update === 'function') {
        el.update();
      }
    });
    
    // Dispatch change event so FreeGiftsButton and other listeners update
    this.dispatchEvent(new Event('change', { bubbles: true }));
  }
  
  _onToggleClicked(event) {
    event?.preventDefault();
    this.open ? this.hide() : this.show();
  }
  
  show(animate = true) {
    if (this.open) {
      return;
    }
    this.setAttribute('open', animate ? '' : 'immediate');
    return this._waitForEvent('dialog:after-show');
  }
  
  hide() {
    if (!this.open) {
      return;
    }
    this.removeAttribute('open');
    return this._waitForEvent('dialog:after-hide');
  }
  
  _waitForEvent(eventName) {
    return new Promise((resolve) => {
      const handler = () => {
        this.removeEventListener(eventName, handler);
        resolve();
      };
      this.addEventListener(eventName, handler, { once: true });
    });
  }
  
  _getClipPathProperties() {
    switch (this.openFrom) {
      case 'left':
        return document.dir === 'ltr'
          ? ['inset(0 100% 0 0 round 0)', 'inset(0 0 0 0 round 0)']
          : ['inset(0 0 0 100% round 0)', 'inset(0 0 0 0 round 0)'];
      case 'right':
        return document.dir === 'ltr'
          ? ['inset(0 0 0 100% round 0)', 'inset(0 0 0 0 round 0)']
          : ['inset(0 100% 0 0 round 0)', 'inset(0 0 0 0 round 0)'];
      case 'bottom':
        return [
          'inset(100% 0 0 0 round var(--drawer-border-radius, 2rem) var(--drawer-border-radius, 2rem) 0 0)',
          'inset(0 0 0 0 round var(--drawer-border-radius, 2rem) var(--drawer-border-radius, 2rem) 0 0)',
        ];
      case 'top':
        return [
          'inset(0 0 100% 0 round var(--rounded-sm))',
          'inset(0 0 0 0 round var(--rounded-sm))',
        ];
    }
  }
  
  _setInitialPosition() {
    this.style.left =
      (document.dir === 'ltr' && this.openFrom === 'left') ||
      (document.dir === 'rtl' && this.openFrom === 'right')
        ? '0px'
        : 'auto';
    this.style.right = this.style.left === 'auto' ? '0px' : 'auto';
    this.style.bottom = this.openFrom === 'bottom' ? '0px' : null;
    this.style.top = this.style.bottom === '' ? '0px' : null;
  }
  
  _showTransition(animate = true) {
    // Hide header before drawer opens (if hide-header attribute is present)
    if (this.hasAttribute('hide-header')) {
      const header = document.querySelector('header');
      if (header) {
        header.style.opacity = '0';
        header.style.visibility = 'hidden';
      }
    }
    
    let animationControls;
    this._setInitialPosition();
    
    if (this.reduceAnimation) {
      animationControls = motionAnimate(
        this,
        { opacity: [0, 1], visibility: ['hidden', 'visible'] },
        { duration: 0.2 }
      );
    } else {
      let content = this.shadowRoot?.querySelector('[part="content"]'),
        sidebar = this.shadowRoot?.querySelector('[part="sidebar"]'),
        closeButton = this.shadowRoot?.querySelector('[part="outside-close-button"]');
      
      animationControls = motionTimeline([
        [
          this,
          { opacity: [0, 1], visibility: ['hidden', 'visible'] },
          { duration: 0.15 },
        ],
        [
          content,
          { clipPath: this._getClipPathProperties() },
          { duration: 0.2, easing: [0.86, 0, 0.07, 1] },
        ],
        [content?.children, { opacity: [0, 1] }, { duration: 0.15 }],
        [sidebar, { opacity: [0, 1] }, { duration: 0.15 }],
        [closeButton, { opacity: [0, 1] }, { at: '<', duration: 0.15 }],
      ]);
    }
    
    animate ? animationControls.play() : animationControls.finish();
    return animationControls.finished.then(() => {
      this.classList.add('show-close-cursor');
      // Set negative inset to allow box-shadow to be visible
      if (!this.reduceAnimation) {
        const content = this.shadowRoot?.querySelector('[part="content"]');
        if (content) {
          content.style.clipPath = 'inset(-30px)';
        }
      }
    });
  }
  
  _hideTransition() {
    // Show header when drawer starts closing (if hide-header attribute is present)
    if (this.hasAttribute('hide-header')) {
      const header = document.querySelector('header');
      if (header) {
        setTimeout(() => {
          header.style.opacity = '1';
          header.style.visibility = 'visible';
        }, 350);
      }
    }
    
    let animationControls;
    if (this.reduceAnimation) {
      animationControls = motionAnimate(
        this,
        { opacity: [1, 0], visibility: ['visible', 'hidden'] },
        { duration: 0.2 }
      );
    } else {
      const content = this.shadowRoot?.querySelector('[part="content"]');
      const sidebar = this.shadowRoot?.querySelector('[part="sidebar"]');
      const closeButton = this.shadowRoot?.querySelector('[part="outside-close-button"]');
      
      animationControls = motionTimeline([
        [closeButton, { opacity: 0 }, { duration: 0.15 }],
        [[...content?.children || []], { opacity: 0 }, { at: '<', duration: 0.15 }],
        [sidebar, { opacity: 0 }, { duration: 0.15 }],
        [
          content,
          { clipPath: this._getClipPathProperties().reverse() },
          { duration: 0.2, easing: [0.86, 0, 0.07, 1] }
        ],
        [
          this,
          { opacity: [1, 0], visibility: ['visible', 'hidden'] },
          { duration: 0.15 }
        ],
      ]);
    }
    return animationControls.finished.then(() =>
      this.classList.remove('show-close-cursor')
    );
  }
  
  _updateSlotVisibility(slot) {
    if (!['header', 'footer', 'body', 'sidebar'].includes(slot.name)) {
      return;
    }
    slot.parentElement.hidden =
      slot.assignedElements({ flatten: true }).length === 0;
  }
  
  _allowOutsideClick(event) {
    if ('TouchEvent' in window && event instanceof TouchEvent) {
      return this._allowOutsideClickTouch(event);
    } else {
      return this._allowOutsideClickMouse(event);
    }
  }
  
  _allowOutsideClickTouch(event) {
    event.target.addEventListener(
      'touchend',
      (subEvent) => {
        const endTarget = document.elementFromPoint(
          subEvent.changedTouches.item(0).clientX,
          subEvent.changedTouches.item(0).clientY
        );
        if (!this.contains(endTarget)) {
          this.hide();
        }
      },
      { once: true }
    );
    return false;
  }
  
  _allowOutsideClickMouse(event) {
    if (event.type !== 'click') {
      return false;
    }
    if (!this.contains(event.target)) {
      this.hide();
    }
    let target = event.target,
      closestControl = event.target.closest('[aria-controls]');
    if (
      closestControl &&
      closestControl.getAttribute('aria-controls') === this.id
    ) {
      target = closestControl;
    }
    return this.id !== target.getAttribute('aria-controls');
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'id':
        if (newValue) {
          this._setupEventListeners();
        } else {
          this._cleanupEventListeners();
        }
        break;
        
      case 'open':
        this.controls.forEach((toggle) =>
          toggle.setAttribute(
            'aria-expanded',
            newValue === null ? 'false' : 'true'
          )
        );
        
        if (oldValue === null && (newValue === '' || newValue === 'immediate')) {
          // Opening
          this.removeAttribute('inert');
          
          const showTransitionPromise =
            this._showTransition(newValue !== 'immediate') || Promise.resolve();
          
          showTransitionPromise.then(() => {
            this.dispatchEvent(new CustomEvent('dialog:after-show', { bubbles: true }));
          });
          
          if (this.shouldTrapFocus) {
            this.focusTrap.activate({
              checkCanFocusTrap: () => showTransitionPromise,
            });
          }
          
          if (this.shouldLock) {
            FreeGiftsDrawer.#lockLayerCount = (FreeGiftsDrawer.#lockLayerCount || 0) + 1;
            this.#isLocked = true;
            document.documentElement.classList.add('lock');
          }
          
        } else if (
          oldValue !== null &&
          newValue === null
        ) {
          // Closing
          this.setAttribute('inert', '');
          const hideTransitionPromise =
            this._hideTransition() || Promise.resolve();
          
          hideTransitionPromise.then(() => {
            this.dispatchEvent(new CustomEvent('dialog:after-hide', { bubbles: true }));
          });
          
          this.focusTrap?.deactivate({
            checkCanReturnFocus: () => hideTransitionPromise,
          });
          
          if (this.shouldLock) {
            this.#isLocked = false;
            FreeGiftsDrawer.#lockLayerCount = Math.max(0, (FreeGiftsDrawer.#lockLayerCount || 0) - 1);
            document.documentElement.classList.toggle(
              'lock',
              FreeGiftsDrawer.#lockLayerCount > 0
            );
          }
        }
        
        this.dispatchEvent(new CustomEvent('toggle', { bubbles: true }));
        break;
    }
  }
}

if (!window.customElements.get('free-gifts-drawer')) {
  window.customElements.define('free-gifts-drawer', FreeGiftsDrawer);
}

